import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useSupabase } from './SupabaseContext';
import { useAuth } from '../hooks/useAuth';
import { products as configProducts } from '../stripe-config';

interface Product {
  id: string;
  name: string;
  description: string;
  prices: Price[];
}

interface Price {
  id: string;
  product_id: string;
  unit_amount: number;
  currency: string;
  recurring_interval: string;
  recurring_interval_count: number;
}

interface Subscription {
  id: string;
  status: string;
  price_id: string;
  product_id: string;
  current_period_end: Date;
  cancel_at_period_end: boolean;
}

type PlanStatus = 
  | 'trial' 
  | 'basic' 
  | 'professional' 
  | 'premium' 
  | 'expired_trial' 
  | 'expired_paid' 
  | 'unknown';

interface StripeContextType {
  products: Product[];
  prices: Price[];
  subscription: Subscription | null;
  isLoading: boolean;
  error: string | null;
  createCheckoutSession: (priceId: string) => Promise<{ url: string } | { error: string }>;
  manageSubscription: () => Promise<{ url: string } | { error: string }>;
  cancelSubscription: () => Promise<{ success: boolean; error?: string }>;
  getUserPlanStatus: (userCreatedAt: string) => PlanStatus;
}

const StripeContext = createContext<StripeContextType | undefined>(undefined);

export const StripeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { supabase } = useSupabase();
  const { user } = useAuth();

  const [products, setProducts] = useState<Product[]>([]);
  const [prices, setPrices] = useState<Price[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Função para carregar produtos, preços e assinatura do usuário
  const fetchSubscriptionData = async () => {
    if (!user) {
      setIsLoading(false);
      setSubscription(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Mock dos produtos a partir da configuração local
      const mockProducts = configProducts.map(p => ({
        id: p.id,
        name: p.name,
        description: p.description,
        prices: [{
          id: p.priceId,
          product_id: p.id,
          unit_amount: parseInt(p.price.replace(/\D/g, '')) || 0,
          currency: 'brl',
          recurring_interval: p.mode === 'subscription' ? 'month' : '',
          recurring_interval_count: p.durationMonths
        }]
      }));

      setProducts(mockProducts);
      setPrices(mockProducts.flatMap(p => p.prices));

      // Busca assinatura via RPC (função remota no Supabase)
      const { data: subscriptionData, error: subscriptionError } = await supabase.rpc('get_user_subscription_status');

      if (subscriptionError) {
        console.error('Erro na RPC de assinatura:', subscriptionError);

        // Caso a RPC falhe por causa da versão do PostgREST, consulta direta
        if (subscriptionError.code === 'PGRST202') {
          const { data: directData, error: directError } = await supabase
            .from('stripe_subscriptions')
            .select(`
              subscription_id,
              price_id,
              status,
              current_period_end,
              cancel_at_period_end,
              stripe_customers!inner(user_id)
            `)
            .eq('stripe_customers.user_id', user.id)
            .eq('status', 'active')
            .maybeSingle();

          if (directError) {
            console.error('Erro na consulta direta:', directError);
            setSubscription(null);
          } else if (directData) {
            setSubscription({
              id: directData.subscription_id,
              status: directData.status,
              price_id: directData.price_id,
              product_id: 'prod_unknown',
              // Ajuste para Date: Supabase pode retornar em timestamp Unix ou ISO string
              current_period_end: directData.current_period_end
                ? (typeof directData.current_period_end === 'number'
                    ? new Date(directData.current_period_end * 1000)
                    : new Date(directData.current_period_end))
                : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
              cancel_at_period_end: directData.cancel_at_period_end
            });
          } else {
            setSubscription(null);
          }
        } else {
          throw subscriptionError;
        }
      } else if (subscriptionData && subscriptionData.length > 0) {
        const subData = subscriptionData[0];
        if (subData.has_active_subscription) {
          setSubscription({
            id: subData.subscription_id ?? 'mock_subscription',
            status: subData.subscription_status ?? 'active',
            price_id: subData.price_id ?? 'price_1RtW0mB0B3nriuwY1nJcHogh',
            product_id: subData.product_id ?? 'prod_SCkLtyiYuAizwO',
            current_period_end: subData.current_period_end
              ? new Date(subData.current_period_end)
              : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
            cancel_at_period_end: subData.cancel_at_period_end ?? false
          });
        } else {
          setSubscription(null);
        }
      } else {
        setSubscription(null);
      }

      // Atualiza a assinatura com dados frescos por segurança
      try {
        // Primeiro tenta buscar via perfil
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('plano_atual, plano_expira_em, stripe_customer_id')
          .eq('id', user.id)
          .single();
          
        if (!profileError && profileData?.plano_atual && profileData?.plano_expira_em) {
          const expirationDate = new Date(profileData.plano_expira_em);
          
          if (!isNaN(expirationDate.getTime()) && expirationDate > new Date()) {
            // Mapear nome do plano para price_id
            const planMap: Record<string, string> = {
              'Premium': 'price_1RtW0mB0B3nriuwY1nJcHogh',
              'Profissional': 'price_1RtW7UB0B3nriuwYofTFEbYJ',
              'Básico': 'price_1RtW9TB0B3nriuwYUyIoSbfp'
            };
            
            setSubscription({
              id: 'profile_subscription',
              status: 'active',
              price_id: planMap[profileData.plano_atual] || 'unknown_price',
              product_id: 'prod_unknown',
              current_period_end: expirationDate,
              cancel_at_period_end: false
            });
          }
          
          // Se temos customer_id no perfil, buscar assinatura diretamente
          if (profileData.stripe_customer_id) {
            const { data: freshData, error: freshError } = await supabase
              .from('stripe_subscriptions')
              .select(`
                subscription_id,
                price_id,
                status,
                current_period_end,
                cancel_at_period_end
              `)
              .eq('customer_id', profileData.stripe_customer_id)
              .eq('status', 'active')
              .maybeSingle();
              
            if (!freshError && freshData) {
              setSubscription({
                id: freshData.subscription_id,
                status: freshData.status,
                price_id: freshData.price_id,
                product_id: 'prod_unknown',
                current_period_end: typeof freshData.current_period_end === 'number'
                  ? new Date(freshData.current_period_end * 1000)
                  : new Date(freshData.current_period_end),
                cancel_at_period_end: freshData.cancel_at_period_end
              });
            }
          }
        }
      } catch (freshError) {
        console.warn('Falha na consulta de perfil:', freshError);
      }

      // Buscar via join tradicional como fallback
      try {
        const { data: freshData, error: freshError } = await supabase
          .from('stripe_subscriptions')
          .select(`
            subscription_id,
            price_id,
            status,
            current_period_end,
            cancel_at_period_end,
            stripe_customers!inner(user_id)
          `)
          .eq('stripe_customers.user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1);

        if (!freshError && freshData && freshData.length > 0) {
          if (!subscription || freshData[0].status === 'active') {
            setSubscription({
              id: freshData[0].subscription_id,
              status: freshData[0].status,
              price_id: freshData[0].price_id,
              product_id: 'prod_unknown',
              current_period_end: typeof freshData[0].current_period_end === 'number'
                ? new Date(freshData[0].current_period_end * 1000)
                : new Date(freshData[0].current_period_end),
              cancel_at_period_end: freshData[0].cancel_at_period_end
            });
          }
        }
      } catch (freshError) {
        console.warn('Falha na consulta de assinatura fresca:', freshError);
      }

      // Verificar dados do perfil para plano_atual e plano_expira_em
      try {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('plano_atual, plano_expira_em, lifetime')
          .eq('id', user.id)
          .single();

        if (!profileError && profileData) {
          // Se temos dados de plano no perfil mas não temos assinatura ativa
          if (profileData.plano_atual && profileData.plano_expira_em && !subscription) {
            const expirationDate = new Date(profileData.plano_expira_em);
            
            // Se o plano ainda está válido
            if (expirationDate > new Date()) {
              // Mapear nome do plano para price_id
              const planMap: Record<string, string> = {
                'Premium': 'price_1RtW0mB0B3nriuwY1nJcHogh',
                'Profissional': 'price_1RtW7UB0B3nriuwYofTFEbYJ',
                'Básico': 'price_1RtW9TB0B3nriuwYUyIoSbfp'
              };
              
              setSubscription({
                id: 'profile_subscription',
                status: 'active',
                price_id: planMap[profileData.plano_atual] || 'unknown_price',
                product_id: 'prod_unknown',
                current_period_end: expirationDate,
                cancel_at_period_end: false
              });
            }
          }
          
          // Se o usuário tem acesso lifetime
          if (profileData.lifetime && !subscription) {
            setSubscription({
              id: 'lifetime_subscription',
              status: 'active',
              price_id: 'price_lifetime',
              product_id: 'prod_lifetime',
              current_period_end: new Date(Date.now() + 100 * 365 * 24 * 60 * 60 * 1000), // 100 years
              cancel_at_period_end: false
            });
          }
        }
      } catch (profileError) {
        console.warn('Falha ao verificar dados do perfil:', profileError);
      }
    } catch (err: any) {
      console.error('Erro geral ao buscar dados Stripe:', err);
      setError(err.message || 'Erro ao carregar dados de assinatura');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionData();

    const subscriptionsChannel = supabase
      .channel('stripe_subscription_changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'stripe_subscriptions'
      }, () => {
        console.log('Mudança em stripe_subscriptions detectada, atualizando...');
        fetchSubscriptionData();
      })
      .subscribe();

    const profilesChannel = supabase
      .channel('profile_changes')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'profiles',
        filter: `id=eq.${user?.id}`
      }, () => {
        console.log('Mudança no perfil detectada, atualizando assinatura...');
        fetchSubscriptionData();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(subscriptionsChannel);
      supabase.removeChannel(profilesChannel);
    };
  }, [supabase, user]);

  // Retorna o status do plano baseado no tempo de criação do usuário e assinatura atual
  const getUserPlanStatus = (userCreatedAt: string): PlanStatus => {
    const createdDate = new Date(userCreatedAt);
    const trialEndDate = new Date(createdDate);
    trialEndDate.setDate(trialEndDate.getDate() + 21); // 21 dias de trial padrão
    const now = new Date();

    if (subscription && subscription.status === 'active') {
      if (subscription.current_period_end > now) {
        if (subscription.price_id === 'price_1RtW0mB0B3nriuwY1nJcHogh') return 'premium';
        if (subscription.price_id === 'price_1RtW7UB0B3nriuwYofTFEbYJ') return 'professional';
        if (
          subscription.price_id === 'price_1RtW9TB0B3nriuwYUyIoSbfp'
        ) return 'basic';
      } else {
        return 'expired_paid';
      }
    }

    if (now <= trialEndDate) return 'trial';

    return 'expired_trial';
  };

  // Cria uma sessão de checkout para um preço específico (invoca a função edge no Supabase)
  const createCheckoutSession = async (priceId: string) => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl || !supabaseUrl.includes('supabase.co')) {
        // Ambiente local / dev: usa mock
        return await createMockCheckoutSession(priceId);
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Sessão expirada. Faça login novamente.');

      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          price_id: priceId,
          success_url: `${window.location.origin}/success`,
          cancel_url: `${window.location.origin}/pricing`
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        console.error('Erro na create-checkout:', error);
        return { error: error.message || 'Erro ao processar pagamento.' };
      }

      if (!data || !data.url) {
        return await createMockCheckoutSession(priceId);
      }

      return data;
    } catch (err: any) {
      console.error('Erro ao criar sessão de checkout:', err);
      return { error: err.message || 'Erro inesperado ao processar pagamento.' };
    }
  };

  // Mock de criação de sessão de checkout para desenvolvimento local
  const createMockCheckoutSession = async (priceId: string) => {
    const selectedProduct = configProducts.find(p => p.priceId === priceId);
    if (!selectedProduct) throw new Error('Plano selecionado não encontrado');

    await new Promise(res => setTimeout(res, 1000)); // Simula delay
    
    // Atualizar o perfil do usuário para indicar que está em processo de checkout
    if (user) {
      try {
        await supabase
          .from('profiles')
          .update({
            checkout_session_id: `mock_session_${Date.now()}`,
            checkout_started_at: new Date().toISOString()
          })
          .eq('id', user.id);
      } catch (err) {
        console.warn('Erro ao atualizar perfil com sessão de checkout mock:', err);
      }
    }
    
    return {
      url: `${window.location.origin}/success?product=${encodeURIComponent(selectedProduct.name)}&price=${encodeURIComponent(selectedProduct.price)}&duration=${selectedProduct.durationMonths}&mock=true`
    };
  };

  // Placeholder para gerenciamento de assinatura
  const manageSubscription = async () => {
    return { error: 'Funcionalidade em desenvolvimento' };
  };

  // Cancela assinatura via função edge
  const cancelSubscription = async () => {
    try {
      if (!user || !subscription?.id) {
        return { success: false, error: 'Nenhuma assinatura ativa encontrada' };
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl || !supabaseUrl.includes('supabase.co')) {
        // Ambiente local / dev: simula cancelamento
        return await cancelMockSubscription();
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        return { success: false, error: 'Sessão expirada. Faça login novamente.' };
      }

      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: { subscriptionId: subscription.id },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        console.error('Erro na cancel-subscription:', error);
        
        // Se o erro é de configuração, tenta cancelamento mock
        if (error.message?.includes('Missing environment variables') || 
            error.message?.includes('STRIPE_SECRET_KEY') ||
            error.message?.includes('Failed to fetch')) {
          return await cancelMockSubscription();
        }
        
        return { success: false, error: error.message || 'Erro ao cancelar assinatura' };
      }

      setSubscription(prev => prev ? { ...prev, cancel_at_period_end: true } : prev);
      return { success: true };
    } catch (err: any) {
      console.error('Erro geral ao cancelar assinatura:', err);
      
      // Se é erro de fetch/conexão, tenta cancelamento mock
      if (err.message?.includes('Failed to fetch') || 
          err.message?.includes('NetworkError') ||
          err.message?.includes('fetch')) {
        return await cancelMockSubscription();
      }
      
      return { success: false, error: err.message || 'Erro ao cancelar assinatura' };
    }
  };

  // Mock de cancelamento de assinatura para desenvolvimento local
  const cancelMockSubscription = async () => {
    try {
      await new Promise(res => setTimeout(res, 1000)); // Simula delay
      
      // Atualizar o perfil do usuário para simular cancelamento
      if (user) {
        const futureDate = new Date();
        futureDate.setDate(futureDate.getDate() + 30); // 30 dias a partir de hoje
        
        await supabase
          .from('profiles')
          .update({
            plano_expira_em: futureDate.toISOString()
          })
          .eq('id', user.id);
      }
      
      // Atualizar estado local
      setSubscription(prev => prev ? { ...prev, cancel_at_period_end: true } : prev);
      
      return { success: true };
    } catch (err: any) {
      console.error('Erro no cancelamento mock:', err);
      return { success: false, error: 'Erro ao processar cancelamento' };
    }
  };

  return (
    <StripeContext.Provider
      value={{
        products,
        prices,
        subscription,
        isLoading,
        error,
        createCheckoutSession,
        manageSubscription,
        cancelSubscription,
        getUserPlanStatus
      }}
    >
      {children}
    </StripeContext.Provider>
  );
};

export const useStripe = (): StripeContextType => {
  const context = useContext(StripeContext);
  if (!context) {
    throw new Error('useStripe deve ser usado dentro de StripeProvider');
  }
  return context;
};